import {
    selectQuestionGroup,
    selectQuestionSettingsIndicators,
} from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { shallowEqual } from "react-redux";
import { useStrings } from "features/i18n/useStrings";
import strings from "./SettingsIndicators.strings";
import "./SettingsIndicators.less";
import { useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { uniqueId } from "lodash";
import { GuxIconIconName } from "genesys-spark-components";
import { GuxIcon, GuxTooltip } from "genesys-spark-components-react";

function Indicator({
    iconName,
    text,
}: {
    iconName: GuxIconIconName;
    text: string;
}) {
    const randomId = uniqueId();

    return (
        <>
            <GuxIcon
                data-testid={`${iconName}-icon-button`}
                className="Indicator"
                id={randomId}
                iconName={iconName}
                screenreaderText={text}
            ></GuxIcon>
            <GuxTooltip for={randomId}>{text}</GuxTooltip>
        </>
    );
}

function ConditionalIndicator() {
    const t = useStrings(strings);
    const formType = useAppSelector((state) => selectFormType(state));
    return (
        <Indicator
            iconName="branch"
            text={
                formType === FormType.SURVEY
                    ? t.includeConditionally
                    : t.displayConditionally
            }
        />
    );
}

function CriticalIndicator() {
    const t = useStrings(strings);
    return <Indicator iconName="alert-info" text={t.criticalQuestion} />;
}

function FatalIndicator() {
    const t = useStrings(strings);
    return (
        <Indicator iconName="alert-warning-triangle" text={t.fatalQuestion} />
    );
}

function NAIndicator() {
    const t = useStrings(strings);
    return <Indicator iconName="fa/circle-check-regular" text={t.naEnabled} />;
}

function RequiredCommentsIndicator() {
    const t = useStrings(strings);
    return <Indicator iconName="comments" text={t.commentsRequired} />;
}

function AssistedQuestionIndicator() {
    const t = useStrings(strings);
    return <Indicator iconName="quote-left" text={t.assistanceConfigured} />;
}

function AiScoringQuestionIndicator() {
    const t = useStrings(strings);
    return (
        <Indicator
            iconName="restricted/custom/sparkles-solid"
            text={t.AiScoringConfigured}
        />
    );
}

export function QuestionSettingsIndicators({
    questionId,
}: {
    questionId: string;
}) {
    const {
        visibilityConditionDependent,
        isCritical,
        isKill,
        naEnabled,
        commentsRequired,
        isAssistanceConfigured,
        isAiScoringConfigured,
    } = useAppSelector(
        (state) => selectQuestionSettingsIndicators(state, questionId),
        shallowEqual
    );

    return (
        <div className="QuestionSettingsIndicators">
            {naEnabled && <NAIndicator />}
            {commentsRequired && <RequiredCommentsIndicator />}
            {isCritical && <CriticalIndicator />}
            {isKill && <FatalIndicator />}
            {visibilityConditionDependent && <ConditionalIndicator />}
            {isAssistanceConfigured && <AssistedQuestionIndicator />}
            {isAiScoringConfigured && <AiScoringQuestionIndicator />}
        </div>
    );
}

export function QuestionGroupSettingsIndicators({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const visibilityConditionDependent = useAppSelector(
        (state) =>
            !!selectQuestionGroup(state, questionGroupId)?.visibilityCondition
                ?.predicates?.length
    );
    const naEnabled = useAppSelector(
        (state) => !!selectQuestionGroup(state, questionGroupId)?.naEnabled
    );
    return (
        <>
            {(naEnabled || visibilityConditionDependent) && (
                <div className="QuestionGroupSettingsIndicators">
                    {naEnabled && <NAIndicator />}
                    {visibilityConditionDependent && <ConditionalIndicator />}
                </div>
            )}
        </>
    );
}
